import { makeAutoObservable, reaction } from "mobx";
import { computedFn } from "mobx-utils";

import { createDirectChat, getChat, getCurrentUserDirectAndGroupChats } from "@/api";
import { onCableConnect, onCableMessage } from "@/api/cable";
import { ChatModel } from "@/models/chat/ChatModel";

import type { Chat } from "@/api";
import type { ChatCreatedPayload } from "@/api/types";
import type { ChatsStatus, CreatingChatStatus } from "@/store/ChatsStore";
import type { RootStore } from "@/store/RootStore";

/** The current user's direct chats, global across teams. */
export class DirectChatsStore {
  private rootStore: RootStore;

  private chatsMap = new Map<string, ChatModel>();

  private cableUnsubscribe?: () => void;

  private cableReconnectUnsubscribe?: () => void;

  private currentUserReactionDispose?: () => void;

  private fetchChatsPromise?: Promise<void>;

  private abortController = new AbortController();

  isSectionOpen = true;

  status: ChatsStatus = "idle";

  creatingChatStatus: CreatingChatStatus = "idle";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeAutoObservable<DirectChatsStore, "abortController">(this, { abortController: false });

    this.cableUnsubscribe = onCableMessage((payload) => {
      if (payload.type === "chat_created_v2") this.handleChatCreated(payload);
    });

    // `chat_created_v2` broadcasts sent while the cable was down are lost
    this.cableReconnectUnsubscribe = onCableConnect((e) => {
      if (e.reconnect) void this.fetchChats();
    });

    // Another tab can switch accounts without reloading this one
    this.currentUserReactionDispose = reaction(
      () => this.rootStore.auth.currentUser?.uid,
      () => this.reset(),
    );
  }

  get chats() {
    return [...this.chatsMap.values()].sort((a, b) =>
      b.lastActivityCursor.localeCompare(a.lastActivityCursor),
    );
  }

  get unreadMessagesCount() {
    return this.chats.reduce((sum, chat) => sum + chat.unreadMessagesCount, 0);
  }

  get hasUnreadChats() {
    return this.chats.some((chat) => chat.unreadMessagesCount > 0);
  }

  chatWith = computedFn((userUid: string): ChatModel | undefined =>
    this.chats.find((chat) => chat.otherParticipant?.uid === userUid),
  );

  getChat(uid: string) {
    return this.chatsMap.get(uid);
  }

  toggleSection() {
    this.isSectionOpen = !this.isSectionOpen;
  }

  setStatus(status: ChatsStatus) {
    this.status = status;
  }

  fetchChats(): Promise<void> {
    // Each ChatModel captures the current user on creation
    if (!this.rootStore.auth.currentUser) return Promise.resolve();

    if (this.fetchChatsPromise) return this.fetchChatsPromise;

    const { signal } = this.abortController;
    const promise = (async () => {
      try {
        this.setStatus("loading");
        const result = await getCurrentUserDirectAndGroupChats({ signal });
        if (signal.aborted) return;

        this.updateChats(result.chats ?? []);
        this.setStatus("loaded");
      } catch {
        if (!signal.aborted) this.setStatus("error");
      }
    })();

    this.fetchChatsPromise = promise;
    void promise.finally(() => {
      if (this.fetchChatsPromise === promise) this.fetchChatsPromise = undefined;
    });
    return promise;
  }

  setCreatingChatStatus(status: CreatingChatStatus) {
    this.creatingChatStatus = status;
  }

  /** Resolve to the chat with the user, creating it on the server when there is none yet. */
  async openChatWith(
    userUid: string,
    { signal }: { signal?: AbortSignal } = {},
  ): Promise<ChatModel | undefined> {
    const existing = this.chatWith(userUid);
    if (existing) return existing;

    try {
      this.setCreatingChatStatus("creating");
      const { chat } = await createDirectChat({ uid: userUid }, { signal });
      if (signal?.aborted || !chat) return undefined;

      this.updateChats([chat]);
      this.setCreatingChatStatus("created");
      return this.chatsMap.get(chat.uid);
    } catch {
      this.setCreatingChatStatus("error");
      return undefined;
    }
  }

  /** Add the direct chats among `chats` and subscribe them for realtime updates. */
  updateChats(chats: Chat[]) {
    chats.forEach((c) => {
      if (c.kind !== "direct" || this.chatsMap.has(c.uid)) return;

      const model = new ChatModel(c, "", this.rootStore.auth.currentUser);
      this.chatsMap.set(c.uid, model);
      model.subscribe();
    });
  }

  private handleChatCreated(payload: ChatCreatedPayload) {
    if (this.chatsMap.has(payload.chat_id)) return;

    const { signal } = this.abortController;
    getChat(payload.chat_id, undefined, { signal })
      .then(({ chat }) => {
        if (!signal.aborted && chat) this.updateChats([chat]);
      })
      .catch((err) => {
        if (!signal.aborted) console.error("Failed to fetch the created direct chat:", err);
      });
  }

  private disposeChats() {
    for (const [uid, chat] of this.chatsMap) {
      chat.dispose();
      this.chatsMap.delete(uid);
    }
  }

  private reset() {
    this.abortController.abort();
    this.abortController = new AbortController();
    this.fetchChatsPromise = undefined;
    this.disposeChats();
    this.status = "idle";
    this.creatingChatStatus = "idle";
  }

  dispose() {
    this.cableUnsubscribe?.();
    this.cableUnsubscribe = undefined;
    this.cableReconnectUnsubscribe?.();
    this.cableReconnectUnsubscribe = undefined;
    this.currentUserReactionDispose?.();
    this.currentUserReactionDispose = undefined;
    this.abortController.abort();
    this.disposeChats();
  }
}
